import {
  ButtonWidth,
  DateFormat,
  FontWeight,
  formatDate,
  formatTime,
} from '@leland-dev/leland-ui-library';
import { type FC } from 'react';

import { formatRecurringCadence } from '../../../../utils/session';
import {
  RadioToggle,
  type RadioToggleOption,
} from '../../../inputs/RadioToggle';

import {
  BaseScheduleSubmissionModalLayout,
  type BaseScheduleSubmissionModalLayoutProps,
} from './BaseScheduleSubmissionModalLayout';
import { type BaseScheduleModalLayoutRecurringCadenceFragment } from './__generated-gql-types__/BaseScheduleModalLayout.generated';

interface RecurringSessionDetailsProps
  extends BaseScheduleSubmissionModalLayoutProps {
  onMoveToSubmissionStep: () => void;
  justThisSession: boolean;
  originalSessionTime: number;
  originalCadence: Nullable<BaseScheduleModalLayoutRecurringCadenceFragment>;
  setJustThisSession: (justThisSession: boolean) => void;
}

export const RecurringSessionDetails: FC<RecurringSessionDetailsProps> = ({
  onMoveToSubmissionStep,
  justThisSession,
  setJustThisSession,
  recurringCadence,
  selectedTime,
  originalSessionTime,
  originalCadence,
  ...baseScheduleModalProps
}) => {
  return (
    <BaseScheduleSubmissionModalLayout
      buttonProps={{
        onClick: onMoveToSubmissionStep,
        width: ButtonWidth.FULL_MOBILE,
      }}
      preventScroll
      sidebarOptions={{
        title: 'Confirm your changes',
        subtitle: 'Choose which sessions should change.',
      }}
      recurringCadence={recurringCadence}
      selectedTime={selectedTime}
      {...baseScheduleModalProps}
    >
      <div className="w-full p-8">
        <h3 className="mb-4 text-lg font-medium">Apply these changes to:</h3>
        <SelectApplyChanges
          onChange={(value) =>
            setJustThisSession(value === 'just-this-session')
          }
          value={justThisSession ? 'just-this-session' : 'all-sessions'}
        />

        <p className="mt-4 text-leland-gray-light">
          Your session on{' '}
          {formatDate(
            originalSessionTime,
            DateFormat.LONG_DAY_OF_WEEK_MONTH_DAY,
          )}{' '}
          at {formatTime(originalSessionTime, { excludeTimeZone: true })} will
          be changed to{' '}
          {formatDate(selectedTime ?? 0, DateFormat.LONG_DAY_OF_WEEK_MONTH_DAY)}
          at {formatTime(selectedTime ?? 0, { excludeTimeZone: true })}.
        </p>
        {originalCadence && recurringCadence ? (
          <p className="mt-5 text-leland-gray-light">
            Other sessions will repeat on{' '}
            {formatRecurringCadence(
              justThisSession ? originalCadence : recurringCadence,
            )}
            .
          </p>
        ) : null}
      </div>
    </BaseScheduleSubmissionModalLayout>
  );
};

interface SelectApplyChangesProps {
  onChange: (value: number | string) => void;
  value: number | Nullable<string>;
}

export const SelectApplyChanges: FC<SelectApplyChangesProps> = ({
  onChange,
  value,
}) => {
  const options: RadioToggleOption[] = [
    {
      label: 'Just this session',
      value: 'just-this-session',
    },
    {
      label: 'This and following sessions',
      value: 'all-sessions',
    },
  ];

  return (
    <RadioToggle
      options={options}
      value={value}
      onChange={onChange}
      name="apply-changes"
      labelFontWeight={FontWeight.MEDIUM}
    />
  );
};
